import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface LabelingState {
  /** Selected cell ids, kept as a map for serializable O(1) lookups */
  selectedCellIds: Record<string, true>
  /** Assigned label per cell (cellId -> label string) */
  labels: Record<string, string>
  /** ISO 8601 timestamp of when each cell was labeled (cellId -> datetime) */
  labeledAt: Record<string, string>
  /** The label that gets applied to selections */
  activeLabel?: string
  /** Labels available in this session (free-text, user-extendable) */
  sessionLabels: string[]
  /** True when there are labels that have not been exported yet */
  dirty: boolean
  /** The run these labels belong to, so state resets when it changes */
  scopeKey?: string
}

export const DEFAULT_SESSION_LABELS = ['fnRBC', 'RBC', 'WBC']

export const labelingInitialState: LabelingState = {
  selectedCellIds: {},
  labels: {},
  labeledAt: {},
  activeLabel: undefined,
  sessionLabels: DEFAULT_SESSION_LABELS,
  dirty: false,
}

export interface AssignLabelPayload {
  label: string
  /** ISO 8601 timestamp captured by the caller (kept out of the reducer) */
  labeledAt: string
}

/** How to resolve cells labeled in both the import file and the current session */
export type ConflictMode = 'current-wins' | 'imported-wins'

export interface ImportEntry {
  cellId: string
  label: string
  labeledAt?: string
}

/** The persistable subset of labeling state (no transient selection). */
export interface LabelingSnapshot {
  labels: Record<string, string>
  labeledAt: Record<string, string>
  sessionLabels: string[]
  activeLabel?: string
  dirty: boolean
}

export const labelingSlice = createSlice({
  name: 'labeling',
  initialState: labelingInitialState,
  reducers: {
    toggleCellSelection: (state, action: PayloadAction<string>) => {
      const cellId = action.payload
      if (state.selectedCellIds[cellId]) {
        delete state.selectedCellIds[cellId]
      } else {
        state.selectedCellIds[cellId] = true
      }
    },
    // Flip the selection for the provided cells: selected become unselected and
    // vice versa. By default (fresh labeling) already-labeled cells are left
    // untouched so a follow-up assignment cannot silently overwrite them. Pass
    // includeLabeled (vetting, where every cell is already labeled) to flip
    // every cell regardless of label.
    invertSelection: (
      state,
      action: PayloadAction<{ cellIds: string[]; includeLabeled?: boolean }>
    ) => {
      const { cellIds, includeLabeled = false } = action.payload
      cellIds.forEach((cellId) => {
        if (!includeLabeled && state.labels[cellId]) return
        if (state.selectedCellIds[cellId]) {
          delete state.selectedCellIds[cellId]
        } else {
          state.selectedCellIds[cellId] = true
        }
      })
    },
    // Toggles selection for many cells at once (e.g. a drag/marquee over a
    // region). Like invertSelection, already-labeled cells are skipped by
    // default; pass includeLabeled (vetting) to include them.
    toggleCellsInRegion: (
      state,
      action: PayloadAction<{ cellIds: string[]; includeLabeled?: boolean }>
    ) => {
      const { cellIds, includeLabeled = false } = action.payload
      cellIds.forEach((cellId) => {
        if (!includeLabeled && state.labels[cellId]) return
        if (state.selectedCellIds[cellId]) {
          delete state.selectedCellIds[cellId]
        } else {
          state.selectedCellIds[cellId] = true
        }
      })
    },
    clearSelection: (state) => {
      state.selectedCellIds = {}
    },
    setActiveLabel: (state, action: PayloadAction<string>) => {
      state.activeLabel = action.payload
    },
    // Assigns a label to every currently selected cell, registers it as a
    // session label, makes it active, and clears the selection so the labeler
    // can move on. Works for both existing and newly created labels.
    assignLabelToSelection: (state, action: PayloadAction<AssignLabelPayload>) => {
      const { label, labeledAt } = action.payload
      if (label && !state.sessionLabels.includes(label)) {
        state.sessionLabels.push(label)
      }
      const selectedIds = Object.keys(state.selectedCellIds)
      selectedIds.forEach((cellId) => {
        state.labels[cellId] = label
        state.labeledAt[cellId] = labeledAt
      })
      state.activeLabel = label
      state.selectedCellIds = {}
      if (selectedIds.length > 0) {
        state.dirty = true
      }
    },
    // Clears the label from every selected cell (vetting: reject a call).
    removeLabelFromSelection: (state) => {
      const selectedIds = Object.keys(state.selectedCellIds)
      selectedIds.forEach((cellId) => {
        delete state.labels[cellId]
        delete state.labeledAt[cellId]
      })
      state.selectedCellIds = {}
      if (selectedIds.length > 0) {
        state.dirty = true
      }
    },
    // Merges imported labels according to the chosen conflict mode.
    importLabels: (
      state,
      action: PayloadAction<{ entries: ImportEntry[]; mode: ConflictMode }>
    ) => {
      const { entries, mode } = action.payload
      entries.forEach(({ cellId, label, labeledAt }) => {
        if (!cellId || !label) return
        // Register every imported label in the dropdown, even if the value is
        // not applied to a cell due to a conflict.
        if (!state.sessionLabels.includes(label)) {
          state.sessionLabels.push(label)
        }
        const hasExisting = Boolean(state.labels[cellId])
        const shouldApply = mode === 'imported-wins' || !hasExisting
        if (shouldApply) {
          state.labels[cellId] = label
          state.labeledAt[cellId] = labeledAt || state.labeledAt[cellId] || ''
        }
      })
      state.dirty = true
    },
    // Called after a successful export so the dirty prompt stops warning.
    markExported: (state) => {
      state.dirty = false
    },
    // Resets all labeling state for a (new) session, so labels/selection from a
    // previously labeled session never bleed into another one.
    resetForScope: (_state, action: PayloadAction<string>) => ({
      selectedCellIds: {},
      labels: {},
      labeledAt: {},
      activeLabel: undefined,
      sessionLabels: [...DEFAULT_SESSION_LABELS],
      dirty: false,
      scopeKey: action.payload,
    }),
    // Restores a previously saved snapshot (e.g. from localStorage) for a run.
    hydrate: (_state, action: PayloadAction<LabelingSnapshot & { scopeKey: string }>) => {
      const { labels, labeledAt, sessionLabels, activeLabel, dirty, scopeKey } = action.payload
      return {
        selectedCellIds: {},
        labels: labels ?? {},
        labeledAt: labeledAt ?? {},
        activeLabel,
        sessionLabels: sessionLabels?.length ? sessionLabels : [...DEFAULT_SESSION_LABELS],
        dirty: dirty ?? false,
        scopeKey,
      }
    },
  },
})

export const LabelingActions = labelingSlice.actions
export const LabelingReducer = labelingSlice.reducer
